/**
 * UnifiedHeader Component
 *
 * Created from HTML: detail_report_after_payment/2)/code.html (lines 38-76)
 * Exact conversion - every class matches HTML design
 *
 * This header is used across all screens for consistent branding and navigation
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '@vsat/shared/store/authStore';
import { useDarkMode } from '../hooks/useDarkMode';
import { AvatarDropdown } from './AvatarDropdown';
import { MobileMenu } from './MobileMenu';

export function UnifiedHeader() {
  const { isAuthenticated, user } = useAuthStore();
  const { isDark, toggle: toggleDarkMode } = useDarkMode();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user) return 'U';
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  // Check if a route is active
  const isActiveRoute = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Logo image - local file
  const logoUrl = '/logo-dinh-huong-nghe-nghiep.png';

  return (
    <>
      <header className='fixed w-full top-0 z-50 bg-white/95 dark:bg-[#1E293B]/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800 h-14 shadow-sm'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full'>
          <div className='flex justify-between items-center h-full'>
            {/* Left: Logo + Branding */}
            <div className='flex items-center gap-3 shrink-0'>
              <Link to='/' className='flex items-center gap-3 shrink-0'>
                <div className='w-12 h-12 flex items-center justify-center overflow-hidden'>
                  <img
                    alt='Logo Viện'
                    className='w-full h-full object-contain'
                    src={logoUrl}
                    onError={e => {
                      // Fallback to icon if image fails to load
                      const target = e.currentTarget;
                      target.style.display = 'none';
                      if (target.parentElement) {
                        target.parentElement.innerHTML =
                          '<span class="material-symbols-outlined text-[#0369A1]">school</span>';
                      }
                    }}
                  />
                </div>
                <div className='flex flex-col justify-center'>
                  <span className='font-bold text-xs text-[#0369A1] dark:text-blue-400 uppercase tracking-tight'>
                    Hướng Nghiệp CAT
                  </span>
                  <span className='text-[10px] leading-none text-[#64748B] dark:text-[#94A3B8] hidden sm:block'>
                    Viện Khảo thí &amp; ĐGCLGD
                  </span>
                </div>
              </Link>
            </div>

            {/* Right: Navigation + Actions */}
            <div className='flex items-center gap-2 md:gap-6 flex-1 justify-end'>
              {/* Navigation Links */}
              <nav className='hidden md:flex items-center gap-1 lg:gap-6 text-[13px] font-medium'>
                <Link
                  to='/ho-so-nang-luc'
                  className={`px-2 py-1 transition-colors relative group/new ${
                    isActiveRoute('/ho-so-nang-luc')
                      ? 'font-bold border-b-2 border-[#7C3AED] dark:border-violet-400'
                      : 'hover:opacity-80'
                  }`}
                >
                  <span
                    className='bg-clip-text text-transparent font-semibold'
                    style={{
                      backgroundImage: 'linear-gradient(135deg, #6366F1, #7C3AED, #EC4899)',
                      backgroundSize: '200% 100%',
                      animation: 'shimmer-text 3s ease-in-out infinite',
                    }}
                  >
                    Hồ sơ năng lực
                  </span>
                  {/* "MỚI" badge */}
                  <span className='absolute -top-1.5 -right-5 text-[8px] font-extrabold bg-red-500 text-white px-1.5 py-0.5 rounded-full leading-none animate-pulse'>
                    MỚI
                  </span>
                </Link>
                <Link
                  to='/huong-nghiep-cat?screen=instruction'
                  className={`px-2 py-1 transition-colors ${
                    isActiveRoute('/huong-nghiep-cat')
                      ? 'text-primary dark:text-orange-400 font-bold border-b-2 border-primary dark:border-orange-400'
                      : 'text-[#64748B] dark:text-[#94A3B8] hover:text-primary dark:hover:text-primary'
                  }`}
                >
                  Khám phá bản thân
                </Link>
                <Link
                  to='/thi-thu-thpt'
                  className={`px-2 py-1 transition-colors ${
                    isActiveRoute('/thi-thu-thpt')
                      ? 'text-primary dark:text-orange-400 font-bold border-b-2 border-primary dark:border-orange-400'
                      : 'text-[#64748B] dark:text-[#94A3B8] hover:text-primary dark:hover:text-primary'
                  }`}
                >
                  Luyện đề THPT
                </Link>
                <Link
                  to='/nganh-hoc'
                  className={`px-2 py-1 transition-colors ${
                    isActiveRoute('/nganh-hoc') || isActiveRoute('/kham-pha-nghe-nghiep')
                      ? 'text-primary dark:text-orange-400 font-bold border-b-2 border-primary dark:border-orange-400'
                      : 'text-[#64748B] dark:text-[#94A3B8] hover:text-primary dark:hover:text-primary'
                  }`}
                >
                  Ngành học
                </Link>
              </nav>

              {/* Divider */}
              <div className='h-5 w-px bg-gray-200 dark:bg-gray-700 hidden md:block'></div>

              {/* Actions */}
              <div className='flex items-center gap-3 pl-1'>
                {/* Mobile Menu Button */}
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  className='text-[#64748B] hover:text-primary transition-colors md:hidden p-2 -mr-2 touch-target'
                  aria-label='Mở menu'
                >
                  <span className='material-symbols-outlined text-[24px]'>menu</span>
                </button>

                {/* Dark Mode Toggle */}
                <button
                  onClick={toggleDarkMode}
                  className='hidden sm:flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-[#64748B] dark:text-gray-300 transition-colors'
                  aria-label='Toggle dark mode'
                >
                  <span className='material-symbols-outlined text-[20px]'>
                    {isDark ? 'light_mode' : 'dark_mode'}
                  </span>
                </button>

                {/* User Avatar */}
                {isAuthenticated ? (
                  <AvatarDropdown
                    userInitials={getUserInitials()}
                    userName={
                      user?.name || (user?.firstName && user?.lastName)
                        ? `${user.firstName} ${user.lastName}`
                        : undefined
                    }
                  />
                ) : (
                  <Link
                    to='/login'
                    className='text-sm font-medium text-[#64748B] dark:text-[#94A3B8] hover:text-primary dark:hover:text-primary transition-colors'
                  >
                    Đăng nhập
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Drawer - Rendered via Portal outside header */}
      {createPortal(
        <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />,
        document.body
      )}
    </>
  );
}
